/** @jsxImportSource @emotion/react */
'use memo'

import {
  AppstoreOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  TableOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import styled from '@emotion/styled'
import type {
  OrgRegistrationFilters as Filters,
  OrgRegistrationTab,
  OrgVenueOption,
} from '@sportninja/common/features/organization/registration/types'
import { matchesVenueOption } from '@sportninja/common/features/organization/registration/utils/venueOptions'
import type { SportSelectGroup } from '@sportninja/common/features/sport/utils/sportSelectOptions'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import FilterSearchInput from 'src/components/ts-components/FilterSearchInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import ViewSwitcher, {
  type ViewSwitcherOption,
} from 'src/components/ts-components/ViewSwitcher'
import type { OrgRegistrationView } from '../types'

interface Props {
  filters: Filters
  sportGroups: SportSelectGroup[]
  sportFilterOption: (
    input: string,
    option: { label?: unknown; options?: unknown } | undefined
  ) => boolean
  venueOptions: OrgVenueOption[]
  /** While the locations load, or if they failed; the list keeps working. */
  venuesDisabled: boolean
  onStatusChange: (status: OrgRegistrationTab) => void
  onSearch: (search: string) => void
  /** Receives the raw select value; the container maps it onto the filters. */
  onSportChange: (value: string) => void
  onVenueChange: (venueId: string) => void
  onShowChildOrganizationsChange: (show: boolean) => void
  view: OrgRegistrationView
  onViewChange: (view: OrgRegistrationView) => void
}

/** Under the shared search box's 320px, leaving the row room for the switches. */
const SEARCH_WIDTH = 200

const STATUS_OPTIONS: ViewSwitcherOption<OrgRegistrationTab>[] = [
  { value: 'open', label: 'Open Now', icon: <UnlockOutlined /> },
  { value: 'upcoming', label: 'Upcoming', icon: <ClockCircleOutlined /> },
  { value: 'all', label: 'All', icon: <AppstoreOutlined /> },
]

const VIEW_OPTIONS: ViewSwitcherOption<OrgRegistrationView>[] = [
  { value: 'cards', label: 'Cards', icon: <IdcardOutlined /> },
  { value: 'list', label: 'List', icon: <TableOutlined /> },
]

/**
 * The tab's filter row: search, sport (every sport, Full Stats and Basic
 * Stats), location (this organization's own venues), a switch to include sub-organizations' registrations (off by
 * default), the Open Now / Upcoming / All switch and the Cards / List
 * switch. Every control reports up; the URL holds the filter values (the view
 * is not remembered).
 */
const OrgRegistrationFilters = ({
  filters,
  sportGroups,
  sportFilterOption,
  venueOptions,
  venuesDisabled,
  onStatusChange,
  onSearch,
  onSportChange,
  onVenueChange,
  onShowChildOrganizationsChange,
  view,
  onViewChange,
}: Props) => {
  'use memo'

  // A shared link's venue that isn't in the list (or not loaded yet) still
  // filters; the field reads All Locations rather than showing a raw id.
  const selectedVenue = venueOptions.some(
    (option) => option.value === filters.venueId
  )
    ? filters.venueId
    : ''

  return (
    <Row>
      <SearchField>
        <FilterSearchInput
          value={filters.search}
          onSearch={onSearch}
          width={SEARCH_WIDTH}
          label={capitalizeWords('Search')}
          // 'Competition' on SportNinja, 'Season' on Canlan.
          placeholder={`Search by ${capitalizeWords(
            t('common:competition', {})
          )}, Team, Division`}
        />
      </SearchField>

      <SelectField>
        <SelectInput
          name='sport'
          label={capitalizeWords('Sport')}
          value={filters.genericSportId || filters.sportId}
          onChange={(value) => onSportChange(value ?? '')}
          options={sportGroups}
          placeholder={capitalizeWords('All sports')}
          fieldErrors={[]}
          customError={{}}
          disabled={false}
          showSearch
          filterOption={sportFilterOption}
          allowClear
        />
      </SelectField>

      <SelectField>
        <SelectInput
          name='location'
          label={capitalizeWords('Location')}
          value={selectedVenue}
          onChange={(value) => onVenueChange(value ?? '')}
          options={venueOptions}
          placeholder={capitalizeWords('All locations')}
          fieldErrors={[]}
          customError={{}}
          disabled={venuesDisabled}
          showSearch
          filterOption={matchesVenueOption}
          allowClear
        />
      </SelectField>

      <SwitchInput
        // 'Within Sub Organization' on SportNinja, 'Within Sub League' on Canlan.
        title={capitalizeWords(`Within Sub ${t('common:organization', {})}`)}
        value={filters.showChildOrganizations}
        onChange={onShowChildOrganizationsChange}
        boxed
      />

      <Switchers>
        <ViewSwitcher<OrgRegistrationTab>
          mode={filters.status}
          onChange={onStatusChange}
          options={STATUS_OPTIONS}
          ariaLabel={capitalizeWords('Registration status')}
        />
        <ViewSwitcher<OrgRegistrationView>
          mode={view}
          onChange={onViewChange}
          options={VIEW_OPTIONS}
          ariaLabel={capitalizeWords('Registrations view')}
        />
      </Switchers>
    </Row>
  )
}

export default OrgRegistrationFilters

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
`

// Sits on the row's baseline with the labelled selects, at SEARCH_WIDTH.
const SearchField = styled.div`
  flex: 0 1 auto;
  min-width: 0;

  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`

// Narrower than the search box, so sport and location both fit the row at
// desktop width; the slack goes between the filters and the status switch.
const SelectField = styled.div`
  flex: 0 1 160px;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`

// Status, then view, taking the rest of whichever line they land on: status
// at its left, view at its right edge — so once they wrap under the filters,
// status lines up with the search box and view with the table's right edge.
const Switchers = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`
